import React, { useEffect, useState } from 'react';
import style from './HealthCheckup.module.css';
import ResultCard from './ResultCard';
import SwitchBox from '../box/SwitchBox.jsx'; 

import axiosApi from '../../api/daonAxios';

import { BODY_ITEMS, BP_ITEMS, LIPID_ITEMS, DIABETES_ITEMS, KIDNEY_ITEMS,} from './HlthcBase';
import noDataImg from '../../images/common/i_3d_no_data_doc.svg';



const formatDate = (date) => {
    if (!date || date.length !== 8) {
        return '';
    }

    return `${date.substring(0, 4)}.${date.substring(4, 6)}.${date.substring(6, 8)}`;
};


const CHECKUP_BASE_ITEMS = [
    ...BODY_ITEMS,
    ...BP_ITEMS,
    ...LIPID_ITEMS,
    ...DIABETES_ITEMS,
    ...KIDNEY_ITEMS,
].filter(Boolean);

const CHECKUP_SECTIONS = [
    {
        title: '신체 계측',
        items: [
            { key: 'bmi', name: 'BMI' },
            { key: 'wastln', name: '허리둘레' },
        ],
    },
    {
        title: '혈액 검사',
        items: [
            { key: 'tchol', name: '총콜레스테롤' },
            { key: 'ldl', name: '저밀도 콜레스테롤' },
            { key: 'hdl', name: '고밀도 콜레스테롤' },
            { key: 'tg', name: '중성지방' },
            { key: 'fbg', name: '혈당' },
            { key: 'hba1c', name: '당화혈색소' },
        ],
    },
    {
        title: '신장기능',
        items: [
            { key: 'bun', name: '요소질소' },
            { key: 'crtnn', name: '혈청크레아티닌' },
            { key: 'egfr', name: 'e-GFR' },
        ],
    },
];

const findBaseItem = (key, gendr) => {
    const sex = gendr === '1' ? 'M' : 'F';

    return CHECKUP_BASE_ITEMS.find(item =>
        item.key === key &&
        (item.sex === 'A' || item.sex === sex)
    );
};

const isNormalValue = (value, base) => {
    if (
        value === null ||
        value === undefined ||
        !base
    ) {
        return false;
    }

    switch (base.condType) {
        case 'bw':
            return value >= base.min && value <= base.max;

        case 'lt':
            return value < base.max;

        case 'le':
            return value <= base.max;

        case 'gt':
            return value > base.min;

        case 'ge':
            return value >= base.min;

        default:
            return true;
    }
};

const makeChartData = (checkupList, key, unit) => {
    return checkupList
        .filter(item =>
            item[key] !== null &&
            item[key] !== undefined
        )
        .map(item => ({
            prscrptnTestRsltDate: item.recDe,
            prscrptnTestRsltVal: item[key],
            unit,
        }))
        .sort((a, b) =>
            b.prscrptnTestRsltDate.localeCompare(
                a.prscrptnTestRsltDate
            )
        );
};

const makeCheckupItem = ({ key, name, currentCheckup, checkupList, gendr,}) => {
    const base = findBaseItem(key, gendr);
    const value = currentCheckup?.[key];

    const hasReference = !!base?.condType;

    const isNormal = hasReference
        ? isNormalValue(value, base)
        : null;

    const currentIndex = checkupList.findIndex(
        item => item.recDe === currentCheckup?.recDe
    );

    const prevValue =
        currentIndex >= 0
            ? checkupList[currentIndex + 1]?.[key]
            : null;

    let comparValue = null;
    let comparType = null;

    if (
        value !== null &&
        value !== undefined &&
        prevValue !== null &&
        prevValue !== undefined
    ) {
        const diff = value - prevValue;

        if (diff > 0) {
            comparType = 'up';
            comparValue = diff;
        } else if (diff < 0) {
            comparType = 'down';
            comparValue = Math.abs(diff);
        } else {
            comparType = 'same';
            comparValue = 0;
        }
    }

    const bookmarkKey = `${key}At`;
    const favorite = currentCheckup?.[bookmarkKey] === '1';

    return {
        key,
        name: name || base?.label || key,
        result: value ?? '-',
        status: isNormal === null
            ? 'normal'
            : isNormal
                ? 'normal'
                : 'abnormal',

        statusText: isNormal === null
            ? '-'
            : isNormal
                ? '정상'
                : '이상',
        ref: base?.refTxt || '-',
        chartData: makeChartData(
            checkupList,
            key,
            base?.unit
        ),
        chartRange: [
            base?.min,
            base?.max,
        ],

        comparValue,
        comparType,

        favorite,
    };
};

const makeCheckupSection = ({ title, items, currentCheckup, checkupList, gendr,}) => ({
    title,
    items: items.map(item =>
        makeCheckupItem({
            ...item,
            currentCheckup,
            checkupList,
            gendr,
        })
    ),
});

const HealthCheckup = () => {

    const [checkupList, setCheckupList] = useState([]);
    const [loading, setLoading] = useState(true);

    const [dateIdx, setDateIdx] = useState(0);
    const [onlyAbnormal, setOnlyAbnormal] = useState(false);

    const currentCheckup = checkupList[dateIdx];
    const currentDate = currentCheckup?.recDe || '';

    const loginUser = JSON.parse(sessionStorage.getItem('loginUser'));

    const brssrTopBase = findBaseItem('brssrTop', loginUser?.gendr);
    const brssrLwetBase = findBaseItem('brssrLwet', loginUser?.gendr);

    const brssrTop = currentCheckup?.brssrTop;
    const brssrLwet = currentCheckup?.brssrLwet;

    const isBloodPressureNormal =
        isNormalValue(brssrTop, brssrTopBase) &&
        isNormalValue(brssrLwet, brssrLwetBase);

    const bloodPressureChartData = checkupList
        .filter(item =>
            item.brssrTop !== null &&
            item.brssrTop !== undefined &&
            item.brssrLwet !== null &&
            item.brssrLwet !== undefined
        )
        .map(item => ({
            prscrptnTestRsltDate: item.recDe,
            brssrTop: item.brssrTop,
            brssrLwet: item.brssrLwet,
            unit: 'mmHg',
        }))
        .sort((a, b) =>
            a.prscrptnTestRsltDate.localeCompare(
                b.prscrptnTestRsltDate
            )
        );

    const currentIndex = checkupList.findIndex(
        item => item.recDe === currentCheckup?.recDe
    );

    const prevCheckup =
        currentIndex >= 0
            ? checkupList[currentIndex + 1]
            : null;

    const makeCompar = (current, prev) => {
        if (
            current === null ||
            current === undefined ||
            prev === null ||
            prev === undefined
        ) {
            return {
                value: null,
                type: null,
            };
        }

        const diff = current - prev;

        return {
            value: Math.abs(diff),
            type:
                diff > 0
                    ? 'up'
                    : diff < 0
                        ? 'down'
                        : 'same',
        };
    };

    const brssrTopCompar = makeCompar(
        brssrTop,
        prevCheckup?.brssrTop
    );

    const brssrLwetCompar = makeCompar(
        brssrLwet,
        prevCheckup?.brssrLwet
    );


    const normalSections = CHECKUP_SECTIONS.map(section =>
        makeCheckupSection({
            ...section,
            currentCheckup,
            checkupList,
            gendr: loginUser?.gendr,
        })
    );

    const sections = [
        normalSections[0],

        {
            title: '혈압',
            items: [
                {
                    key: 'brssr',
                    name: '혈압',
                    result:
                        brssrTop !== undefined &&
                        brssrLwet !== undefined
                            ? `${brssrTop}/${brssrLwet}`
                            : '-',
                    status: isBloodPressureNormal
                        ? 'normal'
                        : 'abnormal',
                    statusText: isBloodPressureNormal
                        ? '정상'
                        : '이상',
                    ref: `${brssrTopBase?.min}~${brssrTopBase?.max} / ${brssrLwetBase?.min}~${brssrLwetBase?.max}`,
                    chartData: bloodPressureChartData,
                    chartRange: [
                        brssrLwetBase?.min,
                        brssrTopBase?.max,
                    ],
                    chartType: 'bloodPressure',
                    comparTopValue: brssrTopCompar.value,
                    comparTopType: brssrTopCompar.type,

                    comparLwetValue: brssrLwetCompar.value,
                    comparLwetType: brssrLwetCompar.type,
                    favorite: currentCheckup?.brssrAt === '1',
                },
            ],
        },

        normalSections[1],

        normalSections[2]

    ];

    const toggleFavorite = (item) => {

        const jToken = localStorage.getItem("jToken");

        const requestOptions = {
            headers: {
                "Authorization": `Bearer ${jToken}`,
            },
        };

        const param = {
            key: item.key,
            favoriteAt: item.favorite ? '0' : '1',
        };

        console.log(param)

        axiosApi.put(`/lab/checkup-favorite`, param, requestOptions).then((res) => {

            getPatntHlthcRslt();

        }).catch(error => {
            console.error(error);
        });
    };

    // 즐겨찾기 → 상단 정렬 + 이상치 필터
    const processedSections = sections
    .map(s => {
        let items = s.items.filter(
            i =>
                i.result !== null &&
                i.result !== undefined &&
                i.result !== '-'
        );

        if (onlyAbnormal) {
            items = items.filter(i => i.status === 'abnormal');
        }

        items.sort((a, b) => {
            const af = a.favorite ? 0 : 1;
            const bf = b.favorite ? 0 : 1;
            return af - bf;
        });

        return { ...s, items };
    })
    .filter(s => s.items.length > 0);


    
    const getPatntHlthcRslt = () => {

        const jToken = localStorage.getItem("jToken");
        const requestOptions = {
            headers: {
                "Authorization": `Bearer ${jToken}`,
            },
        }

        axiosApi.get(`/lab/checkup-result`,requestOptions).then((res) => {

            const result = res.data.result;

            console.log(result);
            setCheckupList(result);
            
        }).catch(error => {
            console.error(error);
            setCheckupList([]);
        }).finally(()=> {
            setLoading(false);
        })
    }

    const downloadCheckupPdf = async () => {
        try {
            const jToken = localStorage.getItem("jToken");

            const response = await axiosApi.get(
                "/lab/checkup-result/pdf",
                {
                    headers: {
                        Authorization: `Bearer ${jToken}`,
                    },
                    responseType: "blob",
                }
            );

            const blob = response.data;

            // 일반 브라우저
            if (!window.AndroidBridge && !window.webkit?.messageHandlers?.downloadFile) {
                const url = window.URL.createObjectURL(blob);

                const link = document.createElement("a");
                link.href = url;
                link.download = "검진결과.pdf";
                link.click();

                window.URL.revokeObjectURL(url);
                return;
            }

            // 웹앱
            const base64 = await blobToBase64(blob);

            const fileName = "검진결과.pdf";

            if (window.AndroidBridge?.downloadFile) {
                window.AndroidBridge.downloadFile(
                    fileName,
                    base64
                );
            } else if (window.webkit?.messageHandlers?.downloadFile) {
                window.webkit.messageHandlers.downloadFile.postMessage({
                    fileName,
                    base64,
                });
            }

        } catch (error) {
            console.error("PDF 다운로드 실패", error);
        }
    };


    const blobToBase64 = (blob) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();

            reader.onloadend = () => {
                const result = reader.result;

                // data:application/pdf;base64, 부분 제거
                const base64 = result.split(",")[1];

                resolve(base64);
            };

            reader.onerror = reject;

            reader.readAsDataURL(blob);
        });
    };

    useEffect(() => {
        getPatntHlthcRslt();
    },[])

    const hasCheckupData = checkupList.length > 0;  // 이상치만 보기 시 이상치가 없을떄 스위치 버튼도 사라짐 방지

    return (
        <div className={style.wrap}>

            {loading ? null : !hasCheckupData ? (
                <>
                       {/* 날짜 네비게이션 */}
                    {/* <div className={style.dateNav}>
                        <button
                            className={style.dateArrow}
                            data-arrow="prev"
                            onClick={() => setDateIdx(i => i - 1)}
                            disabled={dateIdx === 0}
                        ></button>
                        <span className={style.dateText}>검진일 {formatDate(currentDate)}</span>
                        <button
                            className={style.dateArrow}
                            data-arrow="next"
                            onClick={() => setDateIdx(i => i + 1)}
                            disabled={checkupList.length === 0 || dateIdx === checkupList.length - 1}
                        ></button>
                    </div>
                    <div className={style.hosName}>
                        <strong>서울성모병원 </strong>
                    </div>

                    <div className={style.topRow}>
                        <h3 className={style.sectionMainTitle}>주요 검사 결과</h3>
                        <button className={style.pdfBtn} onClick={downloadCheckupPdf}>검진 전체 결과 확인 (PDF)</button>
                    </div>

                    <div className={style.toggleRow}>
                        <div className={style.toggleLabel}>
                            <SwitchBox toggles={onlyAbnormal} status={setOnlyAbnormal} />
                            <span className={style.toggleText}>이상치만 보기</span>
                        </div>
                    </div> */}
                    <div className={style['no-data-box']}>
                        <div className={style['img-box']}>
                            <img src={noDataImg} aria-hidden="true" />
                        </div>
                        <p>등록된 건강검진 기록이 없어요</p>
                        <span>
                            의료진이 건강검진 데이터를 등록하면 <br/>
                            결과를 확인할 수 있어요</span>
                    </div>
                </>
            ) : (
                <>

                    {/* 날짜 네비게이션 */}
                    <div className={style.dateNav}>
                        <button
                            className={style.dateArrow}
                            data-arrow="prev"
                            onClick={() => setDateIdx(i => i - 1)}
                            disabled={dateIdx === 0}
                        ></button>
                        <span className={style.dateText}>검진일 {formatDate(currentDate)}</span>
                        <button
                            className={style.dateArrow}
                            data-arrow="next"
                            onClick={() => setDateIdx(i => i + 1)}
                            disabled={checkupList.length === 0 || dateIdx === checkupList.length - 1}
                        ></button>
                    </div>
                    <div className={style.hosName}>
                        <strong>서울성모병원 </strong>
                    </div>

                    <div className={style.topRow}>
                        <h3 className={style.sectionMainTitle}>주요 검사 결과</h3>
                        <button className={style.pdfBtn} onClick={downloadCheckupPdf}>검진 전체 결과 확인 (PDF)</button>
                    </div>

                    <div className={style.toggleRow}>
                        <div className={style.toggleLabel}>
                            <SwitchBox toggles={onlyAbnormal} status={setOnlyAbnormal} />
                            <span className={style.toggleText}>이상치만 보기</span>
                        </div>
                    </div>

                    
                    {processedSections.length === 0 ? (
                        <div className={style['no-data-box']} data-layout='b'>
                            <div className={style['img-box']}>
                                <img src={noDataImg} aria-hidden="true" />
                            </div>
                            <p> 이상치 항목이 없어요 </p>
                        </div>
                    ) : processedSections.map((section, si) => (
                        <div key={`${currentDate}-${si}`} className={style.section}>
                            <h4 className={style.sectionTitle}>{section.title}</h4>
                            <div className={style.cardList}>
                                {section.items.map((item, ii) => (
                                    <ResultCard
                                        key={`${currentDate}-${si}-${item.name}`}
                                        item={item}
                                        isFavorite={item.favorite}
                                        onToggleFavorite={toggleFavorite}
                                    />
                                ))}
                            </div>
                        </div>
                    ))}
                </> 
            )}
        </div>
    );
};

export default HealthCheckup;